import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './hooks/useAuth'
import Layout from './components/layout/Layout'
import Login from './pages/Login'
import Signup from './pages/Signup'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Dashboard from './pages/Dashboard'
import ApproveSignup from './pages/ApproveSignup'
import Customers from './pages/admin/Customers'
import Users from './pages/admin/Users'
import PendingSignups from './pages/admin/PendingSignups'
import AdminConfig from './pages/admin/AdminConfig'
import Inventory from './pages/admin/Inventory'
import Reorder from './pages/admin/Reorder'
import Shop from './pages/Shop'
import Cart from './pages/Cart'
import CheckoutSuccess from './pages/CheckoutSuccess'
import MyOrders from './pages/MyOrders'
import OrderDetail from './pages/OrderDetail'
import AdminOrders from './pages/admin/AdminOrders'
import PendingPayments from './pages/admin/PendingPayments'
import Fulfillment from './pages/admin/Fulfillment'
import FillOrder from './pages/admin/FillOrder'
import Receiving from './pages/admin/Receiving'
import Reports from './pages/Reports'
import PastReports from './pages/PastReports'
import ChairReports from './pages/admin/ChairReports'
import Account from './pages/Account'
import ImportPastOrders from './pages/admin/ImportPastOrders'
import StarterPackages from './pages/admin/StarterPackages'

/**
 * ProtectedRoute — guards routes behind authentication and (optionally) a
 * specific permission. Mirrors the timesheets app's pattern.
 *
 * If the user isn't logged in, redirect to /login with `?next=` set so they
 * land back here after logging in (used by magic-link emails).
 */
function ProtectedRoute({ children, permission = null }) {
    const { user, loading, isSysAdmin } = useAuth()
    const location = useLocation()

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="spinner w-8 h-8 text-ghasc-navy" />
            </div>
        )
    }

    if (!user) {
        const next = encodeURIComponent(location.pathname + location.search)
        return <Navigate to={`/login?next=${next}`} replace />
    }

    if (isSysAdmin) return children

    if (permission && !user[permission]) {
        return <Navigate to="/" replace />
    }

    return children
}

function AppRoutes() {
    return (
        <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Magic-link approval — public route, but the page itself
          gates the approve/reject buttons by login + sysadmin. */}
            <Route path="/approve/:token" element={<ApproveSignup />} />

            {/* Authenticated routes */}
            <Route path="/" element={
                <ProtectedRoute>
                    <Layout>
                        <Dashboard />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/customers" element={
                <ProtectedRoute permission="isSysAdmin">
                    <Layout>
                        <Customers />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/users" element={
                <ProtectedRoute permission="isSysAdmin">
                    <Layout>
                        <Users />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/signups" element={
                <ProtectedRoute permission="isSysAdmin">
                    <Layout>
                        <PendingSignups />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/config" element={
                <ProtectedRoute permission="isSysAdmin">
                    <Layout>
                        <AdminConfig />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/inventory" element={
                <ProtectedRoute>
                    <Layout>
                        <Inventory />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/orders" element={
                <ProtectedRoute>
                    <Layout>
                        <AdminOrders />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/pending-payments" element={
                <ProtectedRoute>
                    <Layout>
                        <PendingPayments />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/fulfillment" element={
                <ProtectedRoute>
                    <Layout>
                        <Fulfillment />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/fulfillment/:id" element={
                <ProtectedRoute>
                    <Layout>
                        <FillOrder />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/receiving" element={
                <ProtectedRoute>
                    <Layout>
                        <Receiving />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/reorder" element={
                <ProtectedRoute>
                    <Layout>
                        <Reorder />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/reports" element={
                <ProtectedRoute>
                    <Layout>
                        <ChairReports />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/import-past-orders" element={
                <ProtectedRoute permission="isSysAdmin">
                    <Layout>
                        <ImportPastOrders />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/starter-packages" element={
                <ProtectedRoute permission="isSysAdmin">
                    <Layout>
                        <StarterPackages />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/shop" element={
                <ProtectedRoute>
                    <Layout>
                        <Shop />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/cart" element={
                <ProtectedRoute>
                    <Layout>
                        <Cart />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/checkout/success" element={
                <ProtectedRoute>
                    <Layout>
                        <CheckoutSuccess />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/orders" element={
                <ProtectedRoute>
                    <Layout>
                        <MyOrders />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/orders/:id" element={
                <ProtectedRoute>
                    <Layout>
                        <OrderDetail />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/reports" element={
                <ProtectedRoute>
                    <Layout>
                        <Reports />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/reports/history" element={
                <ProtectedRoute>
                    <Layout>
                        <PastReports />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/account" element={
                <ProtectedRoute>
                    <Layout>
                        <Account />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    )
}

export default function App() {
    return (
        <AuthProvider>
            <AppRoutes />
        </AuthProvider>
    )
}
